import { NavigateFunction } from 'react-router-dom';
import { supabase, buildRedirectUrl } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { UserProfile } from './types';

export const createAuthServices = (
  navigate: NavigateFunction,
  toast: ReturnType<typeof useToast>['toast'],
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>
) => {
  // PERFORMANCE: Add profile cache to prevent redundant requests
  let profileCache: { [userId: string]: { data: UserProfile, timestamp: number } } = {};
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const fetchUserProfile = async (userId: string) => {
    try {
      // Check cache first
      const cached = profileCache[userId];
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        if (process.env.NODE_ENV === 'development') {
          console.log("Using cached user profile for:", userId);
        }
        setUserProfile(cached.data);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log("Fetching user profile for:", userId);
      }
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(); // Use maybeSingle to handle 0 rows gracefully

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching user profile:', error);
        }
        return;
      }

      if (data) {
        if (process.env.NODE_ENV === 'development') {
          console.log("User profile found:", data);
        }
        const profile = data as UserProfile;
        setUserProfile(profile);
        // Cache the profile
        profileCache[userId] = { data: profile, timestamp: Date.now() };
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Exception fetching user profile:', error);
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("Signing in with email:", email);
      }
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Login error:", error);
        }
        throw error;
      }
      
      if (!data.session) {
        if (process.env.NODE_ENV === 'development') {
          console.error("No session returned after login");
        }
        throw new Error("Authentication failed");
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Login successful, navigating to marketplace");
      }
      navigate('/marketplace');
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error signing in:', error);
      }
      toast({
        title: "Login failed",
        description: error.message || "An error occurred during login",
        variant: "destructive"
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("Signing out");
      }
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
      navigate('/auth/login');
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error signing out:', error);
      }
      toast({
        title: "Error",
        description: error.message || "An error occurred during logout",
        variant: "destructive"
      });
    }
  };

  const forgotPassword = async (email: string, redirectUrl?: string) => {
    try {
      const finalRedirectTo = redirectUrl || buildRedirectUrl('/auth/reset-password');
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Sending password reset to:", email);
        console.log("Using redirectTo URL:", finalRedirectTo);
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: finalRedirectTo,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password reset email sent",
        description: "Check your inbox for a link to reset your password",
      });
      
      return;
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error in forgot password:', error);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to send reset password email",
        variant: "destructive"
      });
      throw error;
    }
  };

  const updateThemePreference = async (theme: string, userId: string) => {
    try {
      const { error } = await supabase.rpc('update_theme_preference', {
        user_id: userId,
        theme
      });
      
      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error updating theme preference:', error);
        }
        return;
      }
      
      // Update the user profile with new theme preference
      setUserProfile(prev => prev ? { ...prev, theme_preference: theme } : null);
      
      return;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating theme preference:', error);
      }
    }
  };

  return {
    fetchUserProfile,
    signIn,
    signOut,
    forgotPassword,
    updateThemePreference
  };
};